import { DOCUMENT_SLUG_PATTERN_SRC } from './documentMetadata';
import { Linkformat, preprocessMarkdown } from './markdownUtils';

/**
 * Extracted URL with metadata
 */
export interface ExtractedUrl {
  url: string;
  displayText: string;
  domain: string;
  shortUrl: string;
}

/**
 * Extract URLs from markdown and HTML content
 * Uses the same preprocessing as CommonMarkdown for consistency
 * Includes both external URLs and internal legal document references
 */
export function extractUrlsFromContent(content: string): ExtractedUrl[] {
  const urls = new Set<string>();
  const urlMap = new Map<string, string>(); // Map URL to display text

  // Use shared preprocessing function from markdownUtils
  const processedContent = preprocessMarkdown(content);

  // Extract markdown links: [text](url)
  const markdownRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let match;

  while ((match = markdownRegex.exec(processedContent)) !== null) {
    const displayText = match[1];
    const matchedUrl = match[2];
    if (!displayText || !matchedUrl) continue;
    const url = matchedUrl.trim();

    // Convert all URLs to full format using Linkformat (same as CommonMarkdown)
    const fullUrl = Linkformat(url, 'sk');

    // Include valid HTTP/HTTPS URLs and canonical file_id references
    if (isValidCitationTarget(fullUrl)) {
      urls.add(fullUrl);
      if (!urlMap.has(fullUrl)) {
        // Clean up display text - if it's just "link", use the original text
        const cleanText = displayText === 'link' ? url : displayText;
        urlMap.set(fullUrl, cleanText);
      }
    }
  }

  // Extract HTML links: <a href="url">text</a>
  const htmlRegex = /<a[^>]+href=["']([^"']+)["'][^>]*>([^<]*)<\/a>/gi;

  while ((match = htmlRegex.exec(processedContent)) !== null) {
    const displayText = match[2];
    const matchedUrl = match[1];
    if (!matchedUrl) continue;
    const url = matchedUrl.trim();

    // Convert all URLs to full format using Linkformat (same as CommonMarkdown)
    const fullUrl = Linkformat(url, 'sk');

    if (isValidCitationTarget(fullUrl)) {
      urls.add(fullUrl);
      if (!urlMap.has(fullUrl)) {
        urlMap.set(fullUrl, displayText ?? '');
      }
    }
  }

  // Convert to array of ExtractedUrl objects
  return Array.from(urls).map((url) => ({
    url,
    displayText: urlMap.get(url) || shortenUrl(url),
    domain: extractDomain(url),
    shortUrl: shortenUrl(url),
  }));
}

/**
 * Validate if a string is a valid HTTP/HTTPS URL
 */
function isValidHttpUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function isFileIdUrl(urlString: string): boolean {
  return /^file_id:\d+(#.*)?$/i.test(urlString);
}

/** Client-file (Spis) citation scheme — mirrors isFileIdUrl above. */
function isStorageNodeUrl(urlString: string): boolean {
  return /^storage_node:\d+(#.*)?$/i.test(urlString);
}

/**
 * Whether a relative path points at an internal document page (either localized
 * slug: /sk/dokument/…, /sk/document/…, /en/document/…) or a bare /SK/ IRI path.
 *
 * Exported so the slug-agnostic matching (both `dokument` and `document`) is
 * unit-tested directly — a regex regression to `/document/`-only would silently
 * drop every SK `/dokument/` citation from the references sidebar (#5663).
 */
export function isInternalDocumentUrl(urlString: string): boolean {
  // Localized internal routes: /sk/document/..., /sk/dokument/..., /en/document/...
  if (
    new RegExp(
      `^\\/(?:[a-z]{2}\\/)?${DOCUMENT_SLUG_PATTERN_SRC}\\/(?:\\d+|SK\\/[^?#]+)(?:[?#].*)?$`,
      'i'
    ).test(urlString)
  ) {
    return true;
  }

  // Direct IRI-like links that may appear before normalization
  if (/^\/SK\/[^?#]+(?:[?#].*)?$/i.test(urlString)) {
    return true;
  }

  return false;
}

function isValidCitationTarget(urlString: string): boolean {
  return (
    isValidHttpUrl(urlString) ||
    isFileIdUrl(urlString) ||
    isStorageNodeUrl(urlString) ||
    isInternalDocumentUrl(urlString)
  );
}

/**
 * Extract domain from URL
 * Example: https://www.example.com/path -> example.com
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    // Remove www. prefix if present
    return hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Shorten URL for display
 * Example: https://www.example.com/very/long/path#section -> example.com/very/long...#section
 */
function shortenUrl(url: string, maxLength = 50): string {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.replace(/^www\./, '');
    const pathname = urlObj.pathname;
    const search = urlObj.search;
    const hash = urlObj.hash; // Include fragment

    const fullPath = pathname + search + hash;
    const baseUrl = hostname + fullPath;

    if (baseUrl.length <= maxLength) {
      return baseUrl;
    }

    // If too long, truncate the path but try to keep the fragment if possible
    const availableLength = maxLength - hostname.length - hash.length - 3; // -3 for "..."
    if (availableLength > 10) {
      const truncatedPath = (pathname + search).substring(0, availableLength);
      return `${hostname + truncatedPath}...${hash}`;
    }

    // If still too long, show hostname and truncated fragment
    if (hash.length > 0 && hash.length < maxLength - hostname.length - 6) {
      return `${hostname}/...${hash}`;
    }

    return `${hostname}...`;
  } catch {
    return url.substring(0, maxLength) + (url.length > maxLength ? '...' : '');
  }
}
